import React from "react";
import { Select as AntSelect } from "antd";
import { gridFromTags, gridOptionMatches } from "components/Coordination/LocationPicker";

// LEVEL, AREA, GRID, SYSTEM AND DISCIPLINE on the Issues Manager toolbar
// (owner, 2026-10-17: "add filtering per level, Area, Grid, System,
// Discipline ... both on frontend and backend, requests and export").
//
// Each is held as "A,B" - the same shape the tag and people filters use - and
// sent as ?level=A,B; the server returns issues having ANY of the picked
// values, AND across the five (back/utils/issueFieldFilters). The options
// are the values actually on the issues in scope (GET /issue/filter-options),
// with their counts, so every choice returns rows. "No level" (and so on)
// is offered when some issues carry none, sent as NONE.

export const FIELD_FILTER_KEYS = ["level", "area", "grid", "system", "discipline"];
export const NONE = "__none__";
const LABELS = { level: "Level", area: "Area", grid: "Grid", system: "System", discipline: "Discipline" };

export const emptyFieldFilters = () => FIELD_FILTER_KEYS.reduce((o, k) => ({ ...o, [k]: "" }), {});
export const fieldFiltersFromParams = (params) => FIELD_FILTER_KEYS.reduce((o, k) => ({ ...o, [k]: params.get(k) || "" }), {});
/** The query params for the list, the bulk tools and every export. */
export const fieldFilterParams = (f) => FIELD_FILTER_KEYS.reduce((o, k) => ({ ...o, [k]: (f && f[k]) || undefined }), {});
export const anyFieldFilter = (f) => FIELD_FILTER_KEYS.some((k) => f && f[k]);

/** The dropdown's options for one filter, with any picked value no issue lists. */
export function fieldOptionList(k, opt, picked) {
  const label = LABELS[k];
  const list = [
    ...(opt.none ? [{ value: NONE, label: `No ${label.toLowerCase()} (${opt.none})` }] : []),
    ...(opt.values || []).map((x) => ({ value: x.value, label: `${x.value} (${x.count})` })),
  ];
  // A value picked from a link or a saved filter that is no longer on any
  // issue still shows as picked, rather than as a bare value with no label.
  // A TYPED GRID LOCATION has no count of its own: "H-32" finds an issue
  // written "H/32-33", which is a different option - "(0)" would be wrong.
  for (const p of picked) if (!list.some((o) => o.value === p)) list.push({ value: p, label: k === "grid" && p !== NONE ? p : `${p === NONE ? `No ${label.toLowerCase()}` : p} (0)` });
  return list;
}

export default function IssueFieldFilters({ value, options, onChange }) {
  // GRID TAKES TYPED LOCATIONS TOO (owner, 2026-10-18): "I-40", "40-I",
  // "I40" are one place to the server (back/utils/gridRefs), so a location
  // no issue lists yet in that spelling can still be asked for, and several
  // can be typed at once: "I-40, L-50".
  const set = (k, v) => onChange({ ...value, [k]: k === "grid"
    ? gridFromTags((v || []).filter((x) => x !== NONE)).split(", ").filter(Boolean).concat((v || []).includes(NONE) ? [NONE] : []).join(",")
    : (v || []).join(",") });
  return FIELD_FILTER_KEYS.map((k) => {
    const label = LABELS[k];
    const opt = (options && options[k]) || { values: [], none: 0 };
    const picked = value && value[k] ? String(value[k]).split(",").filter(Boolean) : [];
    const list = fieldOptionList(k, opt, picked);
    return (
      <AntSelect
        key={k}
        mode={k === "grid" ? "tags" : "multiple"}
        {...(k === "grid" ? { tokenSeparators: [",", ";"], filterOption: gridOptionMatches } : {})}
        maxTagCount="responsive"
        aria-label={`${label} filter`}
        placeholder={label}
        allowClear
        showSearch
        optionFilterProp="label"
        value={picked}
        onChange={(v) => set(k, v)}
        style={{ width: 160 }}
        popupMatchSelectWidth={false}
        title={k === "grid"
          ? "Issues at ANY of the picked grid locations - pick, or type one or several: I-40, L-50 (a letter and a number, either order). A line alone (I) finds every location on it. The export follows this."
          : `Issues on ANY of the picked ${label.toLowerCase()} values. The export follows this.`}
        options={list}
        notFoundContent={`No ${label.toLowerCase()} on these issues yet`}
      />
    );
  });
}
